import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import {
  API_BASE_URL,
  API_ENDPOINTS,
  STORAGE_KEYS,
  ERROR_MESSAGES,
  PERFORMANCE,
} from '@utils/constants';
import { getStorageItem, setStorageItem, removeStorageItem } from '@utils/helpers';
import type {
  ApiResponse,
  ApiError,
  User,
  Event,
  Photo,
  Order,
  OrderHistoryItem,
  OrderHistoryFilters,
  LoginForm,
  RegisterForm,
  CheckoutForm,
} from '@/types';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: PERFORMANCE.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token management
let authToken: string | null = getStorageItem(STORAGE_KEYS.AUTH_TOKEN, null);

// Tenant cache for photographer ID header
let cachedPhotographerId: string | null = null;

// Request interceptor to add auth token and photographer ID header
api.interceptors.request.use(
  async config => {
    // Add auth token if available
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    // Add photographer ID header for multi-tenant requests
    // Skip for tenant resolution endpoint itself to avoid circular dependency
    if (!config.url?.includes('/gallery/config')) {
      try {
        // Get photographer ID from tenant service cache if available
        if (!cachedPhotographerId) {
          // Lazy import to avoid circular dependency
          const { tenantService } = await import('./tenant');
          const cachedConfig = tenantService.getCachedConfig();
          if (cachedConfig) {
            cachedPhotographerId = cachedConfig.photographerId;
          }
        }

        if (cachedPhotographerId) {
          config.headers['x-photographer-id'] = cachedPhotographerId;
        }
      } catch {
        // Don't block requests if photographer ID unavailable
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

// Auth endpoints that should NOT trigger token refresh on 401
// These are login/registration flows where user doesn't have a valid session yet
const AUTH_ENDPOINTS_NO_REFRESH = [
  '/auth/login',
  '/auth/register',
  '/auth/magic-link/verify',
  '/auth/magic-link/request',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/auth/refresh',
];

// Response interceptor for token refresh and error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    const requestUrl = originalRequest?.url || '';

    // Skip token refresh for auth endpoints (login/register flows)
    const isAuthEndpoint = AUTH_ENDPOINTS_NO_REFRESH.some(endpoint => requestUrl.includes(endpoint));

    // Handle 401 errors (token expired) - but not for auth endpoints
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        // Use the REFRESH_TOKEN (not the access token) for refresh operations
        const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN, null);
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.AUTH_REFRESH}`, {
            refresh_token: refreshToken,
          });

          // Backend returns new access_token and refresh_token
          const newAccessToken = response.data.tokens?.access_token || response.data.access_token;
          const newRefreshToken = response.data.tokens?.refresh_token || response.data.refresh_token;

          // Update stored tokens - rotate refresh token for security
          setAuthToken(newAccessToken);
          if (newRefreshToken) {
            setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
          }

          // Retry original request with new access token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear all auth data and redirect to login
        clearAuthToken();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    const apiError: ApiError = {
      message: getErrorMessage(error),
      code: error.response?.status?.toString(),
      details: error.response?.data,
    };

    return Promise.reject(apiError);
  }
);

// Helper function to get error message
const getErrorMessage = (error: AxiosError): string => {
  if (error.response?.data && typeof error.response.data === 'object') {
    const data = error.response.data as any;
    return data.message || data.error || ERROR_MESSAGES.GENERIC;
  }

  if (error.code === 'ECONNABORTED') {
    return 'Request timeout. Please try again.';
  }

  if (error.code === 'ERR_NETWORK') {
    return ERROR_MESSAGES.NETWORK;
  }

  return error.message || ERROR_MESSAGES.GENERIC;
};

// Token management functions
export const setAuthToken = (token: string): void => {
  authToken = token;
  setStorageItem(STORAGE_KEYS.AUTH_TOKEN, token);
};

export const setRefreshToken = (token: string): void => {
  setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, token);
};

export const getRefreshToken = (): string | null => {
  return getStorageItem(STORAGE_KEYS.REFRESH_TOKEN, null);
};

export const clearAuthToken = (): void => {
  authToken = null;
  removeStorageItem(STORAGE_KEYS.AUTH_TOKEN);
  removeStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
  removeStorageItem(STORAGE_KEYS.USER_DATA);
};

// Generic API request function with retry logic
const apiRequest = async <T>(
  requestFn: () => Promise<AxiosResponse<ApiResponse<T>>>,
  retries: number = PERFORMANCE.RETRY_ATTEMPTS
): Promise<ApiResponse<T>> => {
  try {
    const response = await requestFn();
    return response.data;
  } catch (error) {
    if (retries > 0 && shouldRetry(error as AxiosError)) {
      await delay(1000 * (PERFORMANCE.RETRY_ATTEMPTS - retries + 1));
      return apiRequest(requestFn, retries - 1);
    }
    throw error;
  }
};

const shouldRetry = (error: AxiosError): boolean => {
  // Retry on network errors and 5xx status codes
  return !error.response || error.response.status >= 500 || error.code === 'ERR_NETWORK';
};

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// Two-Factor Authentication API
export const twoFactorAPI = {
  // Initiate 2FA setup - returns QR code and backup codes
  setup: async (): Promise<{
    secret: string;
    qr_code_uri: string;
    backup_codes: string[];
  }> => {
    const response = await api.post(API_ENDPOINTS.AUTH_2FA_SETUP);
    return response.data;
  },

  // Verify TOTP code and enable 2FA
  verify: async (code: string): Promise<{ message: string }> => {
    const response = await api.post(API_ENDPOINTS.AUTH_2FA_VERIFY, { code });
    return response.data;
  },

  // Disable 2FA (requires password and current 2FA code)
  disable: async (password: string, code: string): Promise<{ message: string }> => {
    const response = await api.post(API_ENDPOINTS.AUTH_2FA_DISABLE, { password, code });
    return response.data;
  },

  // Get 2FA status
  getStatus: async (): Promise<{
    enabled: boolean;
    backup_codes_remaining: number;
  }> => {
    const response = await api.get(API_ENDPOINTS.AUTH_2FA_STATUS);
    return response.data;
  },

  // Regenerate backup codes (requires current 2FA code)
  regenerateBackupCodes: async (code: string): Promise<string[]> => {
    const response = await api.post(API_ENDPOINTS.AUTH_2FA_BACKUP_CODES, { code });
    return response.data;
  },
};

// Authentication API
export const authAPI = {
  login: async (credentials: LoginForm): Promise<{ user: User; tokens: any }> => {
    const response = await api.post(API_ENDPOINTS.AUTH_LOGIN, credentials);
    return response.data.data;
  },

  register: async (userData: RegisterForm): Promise<{ user: User; tokens: any }> => {
    const response = await api.post(API_ENDPOINTS.AUTH_REGISTER, userData);
    return response.data.data;
  },

  refreshToken: async (): Promise<{ tokens: any }> => {
    // Get the stored refresh token
    const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN, null);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const response = await api.post(API_ENDPOINTS.AUTH_REFRESH, {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  logout: async (): Promise<ApiResponse<void>> => {
    return apiRequest(() => api.post(API_ENDPOINTS.AUTH_LOGOUT));
  },

  getProfile: async (): Promise<ApiResponse<User>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.AUTH_PROFILE));
  },

  updateProfile: async (updates: Partial<User>): Promise<ApiResponse<User>> => {
    return apiRequest(() => api.patch(API_ENDPOINTS.AUTH_PROFILE, updates));
  },

  changePassword: async (data: {
    current_password: string;
    new_password: string;
  }): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    return apiRequest(() => api.post('/auth/change-password', data));
  },

  deleteAccount: async (data: {
    password?: string;
    confirmation: string;
  }): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    return apiRequest(() => api.delete(API_ENDPOINTS.AUTH_PROFILE, { data }));
  },

  // GDPR Data Export - Download all user data
  exportData: async (): Promise<{
    success: boolean;
    message: string;
    data: {
      export_metadata: {
        export_date: string;
        export_version: string;
        data_controller: string;
        user_email: string;
        gdpr_article: string;
        format: string;
      };
      profile: {
        user_id: string;
        email: string;
        first_name: string;
        last_name: string;
        role: string;
        permissions: string[];
        is_active: boolean;
        email_verified: boolean;
        two_factor_enabled: boolean;
        auth_provider: string;
        linked_providers: string[];
        account_created: string | null;
        account_updated: string | null;
        last_login: string | null;
        email_verified_at: string | null;
      };
      orders: Array<{
        order_id: string;
        event_id: string;
        status: string;
        created_at: string;
        paid_at: string | null;
        customer_info: {
          first_name: string;
          last_name: string;
          email: string;
          phone: string | null;
          company: string | null;
        };
        items: Array<{
          media_id: string;
          title: string;
          filename: string;
          resolution: string;
          price: number;
          quantity: number;
          subtotal: number;
        }>;
        pricing: {
          subtotal: number;
          discount: number;
          discount_code: string | null;
          tax: number;
          tax_rate: number;
          total: number;
          currency: string;
        };
        payment: {
          method: string;
          status: string;
          amount: number;
          currency: string;
          payment_method_type: string | null;
          last4: string | null;
          processed_at: string | null;
        };
        delivery: {
          method: string;
          status: string;
          download_count: number;
          last_download: string | null;
          delivered_at: string | null;
        };
      }>;
      activity: {
        account_created: string | null;
        last_login: string | null;
        email_verified_at: string | null;
        two_factor_enabled: boolean;
        auth_provider_history: string[];
      };
    };
  }> => {
    const response = await api.get(API_ENDPOINTS.AUTH_DATA_EXPORT);
    return response.data;
  },
};

// Events API
export const eventAPI = {
  getEvents: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<Event[]>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.EVENTS, { params }));
  },

  getEvent: async (id: string): Promise<ApiResponse<Event>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.EVENT_BY_ID(id)));
  },

  getFeaturedEvents: async (): Promise<ApiResponse<Event[]>> => {
    return apiRequest(() => api.get(`${API_ENDPOINTS.EVENTS}/featured`));
  },

  getPhotos: async (
    eventId: string,
    params?: {
      page?: number;
      limit?: number;
      tags?: string[];
    }
  ): Promise<ApiResponse<{ photos: Photo[]; pagination: any }>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.EVENT_PHOTOS(eventId), { params }));
  },
};

// Photos API
export const photoAPI = {
  getPhoto: async (id: string): Promise<ApiResponse<Photo>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.PHOTO_BY_ID(id)));
  },

  searchPhotos: async (params: {
    query?: string;
    eventId?: string;
    tags?: string[];
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{ photos: Photo[]; pagination: any }>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.PHOTOS, { params }));
  },

  getFeaturedPhotos: async (): Promise<ApiResponse<Photo[]>> => {
    return apiRequest(() => api.get(`${API_ENDPOINTS.PHOTOS}/featured`));
  },
};

// Orders API
export const orderAPI = {
  createPaymentIntent: async (orderData: {
    items: Array<{
      photoId: string;
      resolution: string;
      quantity: number;
    }>;
    customerInfo?: CheckoutForm;
    discountCode?: string;
  }): Promise<ApiResponse<{ clientSecret: string; orderId: string }>> => {
    return apiRequest(() => api.post(API_ENDPOINTS.CREATE_PAYMENT_INTENT, orderData));
  },

  confirmOrder: async (orderId: string, paymentIntentId: string): Promise<ApiResponse<Order>> => {
    return apiRequest(() =>
      api.post(`${API_ENDPOINTS.ORDER_BY_ID(orderId)}/confirm`, { paymentIntentId })
    );
  },

  getOrder: async (id: string): Promise<ApiResponse<Order>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.ORDER_BY_ID(id)));
  },

  getUserOrders: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<{ orders: Order[]; pagination: any }>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.ORDERS, { params }));
  },

  getOrderHistory: async (
    filters?: OrderHistoryFilters & { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }
  ): Promise<ApiResponse<{ orders: OrderHistoryItem[]; pagination: any }>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.ORDERS, { params: filters }));
  },

  downloadPhoto: async (orderId: string, photoId: string): Promise<Blob> => {
    const response = await api.get(`${API_ENDPOINTS.ORDER_BY_ID(orderId)}/download/${photoId}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Cart API
export const cartAPI = {
  validateDiscount: async (
    code: string,
    subtotal: number
  ): Promise<
    ApiResponse<{
      valid: boolean;
      discount: number;
      description?: string;
    }>
  > => {
    return apiRequest(() => api.post(API_ENDPOINTS.VALIDATE_DISCOUNT, { code, subtotal }));
  },
};

// Admin API (protected routes)
export const adminAPI = {
  getStats: async (): Promise<
    ApiResponse<{
      totalEvents: number;
      totalOrders: number;
      totalRevenue: number;
      totalCustomers: number;
    }>
  > => {
    return apiRequest(() => api.get(API_ENDPOINTS.ADMIN_STATS));
  },

  getEvents: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<{ events: Event[]; pagination: any }>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.ADMIN_EVENTS, { params }));
  },

  createEvent: async (eventData: Partial<Event>): Promise<ApiResponse<Event>> => {
    return apiRequest(() => api.post(API_ENDPOINTS.ADMIN_EVENTS, eventData));
  },

  updateEvent: async (id: string, updates: Partial<Event>): Promise<ApiResponse<Event>> => {
    return apiRequest(() => api.patch(`${API_ENDPOINTS.ADMIN_EVENTS}/${id}`, updates));
  },

  deleteEvent: async (id: string): Promise<ApiResponse<void>> => {
    return apiRequest(() => api.delete(`${API_ENDPOINTS.ADMIN_EVENTS}/${id}`));
  },

  getOrders: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<{ orders: Order[]; pagination: any }>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.ADMIN_ORDERS, { params }));
  },

  updateOrderStatus: async (id: string, status: string): Promise<ApiResponse<Order>> => {
    return apiRequest(() => api.patch(`${API_ENDPOINTS.ADMIN_ORDERS}/${id}`, { status }));
  },

  getCustomers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<{ customers: User[]; pagination: any }>> => {
    return apiRequest(() => api.get(API_ENDPOINTS.ADMIN_CUSTOMERS, { params }));
  },

  uploadPhotos: async (eventId: string, files: FileList): Promise<ApiResponse<Photo[]>> => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('photos', file);
    });

    return apiRequest(() =>
      api.post(`${API_ENDPOINTS.ADMIN_EVENTS}/${eventId}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    );
  },
};

// Pricing Configuration API
export const pricingAPI = {
  getConfiguration: async (photographerId?: string): Promise<ApiResponse<any>> => {
    const params = photographerId ? { photographer_id: photographerId } : {};
    return apiRequest(() => api.get(API_ENDPOINTS.PRICING_CONFIG, { params }));
  },

  updateBasePrices: async (basePrices: {
    singlePhoto: number;
    smallBundle: number;
    mediumBundle: number;
    largeBundle: number;
    fullEvent: number;
  }): Promise<ApiResponse<any>> => {
    return apiRequest(() => api.put(API_ENDPOINTS.PRICING_BASE, { basePrices }));
  },

  updateVolumeDiscounts: async (
    volumeDiscounts: Array<{ photoCount: number; discountPercent: number }>
  ): Promise<ApiResponse<any>> => {
    return apiRequest(() => api.put(API_ENDPOINTS.PRICING_DISCOUNTS, { volumeDiscounts }));
  },

  updateEventMultipliers: async (
    eventTypeMultipliers: Array<{ eventType: string; multiplier: number; displayName: string }>
  ): Promise<ApiResponse<any>> => {
    return apiRequest(() => api.put(API_ENDPOINTS.PRICING_MULTIPLIERS, { eventTypeMultipliers }));
  },

  updateSpecialOffers: async (
    specialOffers: Array<{
      id: string;
      name: string;
      discountType: 'percentage' | 'fixed';
      discountValue: number;
      conditions?: {
        minPhotos?: number;
        maxPhotos?: number;
        eventTypes?: string[];
        timeLimit?: number;
      };
      isActive: boolean;
      validUntil?: string;
    }>
  ): Promise<ApiResponse<any>> => {
    return apiRequest(() => api.put(API_ENDPOINTS.PRICING_OFFERS, { specialOffers }));
  },

  updateCommission: async (photographerCommission: number): Promise<ApiResponse<any>> => {
    return apiRequest(() => api.put(API_ENDPOINTS.PRICING_COMMISSION, { photographerCommission }));
  },
};

// Health check
export const healthCheck = async (): Promise<boolean> => {
  try {
    await api.get('/health');
    return true;
  } catch {
    return false;
  }
};

// Export the main api instance for custom requests
export default api;
